import { formatDate, formatList } from '@platform/design-system';
import type { Operation, ResponseBody } from '@platform/openapi';
import type { TFunction } from 'i18next';
import { ORDER_VIEWS, type OrderView } from '../orders/orders.ts';

export type NotificationList = ResponseBody<Operation<'/admin/notifications', 'get'>>;
export type Notification = NotificationList['items'][number];
export type NotificationKind = Notification['kind'];

export type NotificationTarget =
  | { readonly kind: 'order'; readonly orderId: string }
  | { readonly kind: 'product'; readonly productId: string }
  | { readonly kind: 'view'; readonly view: OrderView };

export type NotificationText = { readonly title: string; readonly detail: string };

type Params = Notification['params'];

const textOf = (params: Params, key: string): string => String(params[key] ?? '');

const countOf = (params: Params, key: string): number => Number(params[key] ?? 0);

const isOrderView = (value: string): value is OrderView => (ORDER_VIEWS as readonly string[]).includes(value);

export const targetOf = (notification: Notification): NotificationTarget => {
  const { subject, params } = notification;
  if (subject.type === 'order') return { kind: 'order', orderId: subject.id };
  if (subject.type === 'stock_item') return { kind: 'product', productId: textOf(params, 'productId') };
  return { kind: 'view', view: isOrderView(subject.id) ? subject.id : 'all' };
};

const DIGEST_FIGURES = ['missing', 'deliveryFailed', 'returnsToReceive'] as const;

type TextBuilder = (params: Params, t: TFunction, locale: string) => NotificationText;

const stockText =
  (kind: 'stock_low' | 'stock_out'): TextBuilder =>
  (params, t) => {
    const values = {
      product: textOf(params, 'productName'),
      sku: textOf(params, 'sku'),
      depot: textOf(params, 'depotName'),
      available: countOf(params, 'available'),
      threshold: countOf(params, 'threshold'),
    };
    return {
      title: t(`notifications.${kind}_title`, values),
      detail: t(`notifications.${kind}_detail`, values),
    };
  };

const TEXT_OF_KIND: Readonly<Record<NotificationKind, TextBuilder>> = {
  stock_low: stockText('stock_low'),
  stock_out: stockText('stock_out'),
  dispatch_failed: (params, t) => ({
    title: t('notifications.dispatch_failed_title', { number: textOf(params, 'number') }),
    detail: t('notifications.dispatch_failed_detail', {
      reason: t(`errors.${textOf(params, 'failure')}`, { defaultValue: t('notifications.unknownReason') }),
    }),
  }),
  carrier_canceled: (params, t) => ({
    title: t('notifications.carrier_canceled_title', { number: textOf(params, 'number') }),
    detail: t('notifications.carrier_canceled_detail'),
  }),
  daily_digest: (params, t, locale) => ({
    title: t('notifications.daily_digest_title', { day: formatDate(textOf(params, 'day'), locale, 'long') }),
    detail: formatList(
      DIGEST_FIGURES.filter((figure) => countOf(params, figure) > 0).map((figure) =>
        t(`notifications.digest_${figure}`, { count: countOf(params, figure) }),
      ),
      locale,
    ),
  }),
};

export const notificationText = (
  notification: Notification,
  t: TFunction,
  locale: string,
): NotificationText => TEXT_OF_KIND[notification.kind](notification.params, t, locale);
